import React, { cloneElement, CSSProperties, useState } from 'react';
import { SubMenuItemComponentProps } from 'components/side-menu/sub-menu-item/sub-menu-item.props';
import { Distance, Divider, Flex } from 'common/common-components.styled';
import Tooltip from '@mui/material/Tooltip';
import { SubMenuItem } from 'components/side-menu/side-menu-interfaces';
import { useTypedSelector } from 'hooks/use-typed-selector';
import { AnyObject } from 'common/interfaces';
import { IndicatorSelector } from 'selectors';
import { SubMainMenuTitles } from 'components/side-menu/side-menu-enums';
import { setSideMenuOpen } from 'actions/app_action';
import { useHistory, useLocation } from 'react-router-dom';
import { useUnsavedChangesModal } from 'hooks/modals/unsaved-changes.modal/unsaved-changes.modal';
import { useDispatch } from 'react-redux';
import { useTheme } from 'styled-components';
import IconHover from 'components/icon-hover';
import {
  StyledIndicator,
  StyledIndicatorForCollapsedMenu,
  StyledMenuTitle,
  StyledSubMenuItem,
  StyledSubMenuLinkItem
} from 'components/side-menu/sub-menu-item/sub-menu-item.styled';
import InfoChip from 'components/info-chip';
import { IndicatorTypes } from '@/request-config/indicators/indicators.types';
import { replaceGermanWordsToEnglish } from 'utils/replace-german-words-to-english';
import usePaginationState from 'hooks/use-pagination-state/use-pagination-state';
import { PAGES } from 'common/enums';

const SubMenuItemComponent = ({
  subMenuItem,
  showMenuTitle,
  closePopover,
  isLastItem,
  isFirstItem
}: SubMenuItemComponentProps) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const indicator: AnyObject = useTypedSelector(IndicatorSelector);

  const { openUnsavedChangesModal } = useUnsavedChangesModal();
  const history = useHistory();
  const location = useLocation();
  const { goToFirstPage } = usePaginationState({});

  const path =
    location.pathname[location.pathname.length - 1] === '/' ? location.pathname.slice(0, -1) : location.pathname;

  const [showTooltip, setShowTooltip] = useState(false);

  const checkIndicator = (menuItem: SubMenuItem) => {
    let isIndicatorShown = false;
    let indicatorValue: number | null = null;

    if (menuItem.indicator && indicator) {
      let currentIndicator: number | null = null;
      switch (menuItem.title) {
        case SubMainMenuTitles.IncomingInvoices:
          currentIndicator = indicator[IndicatorTypes.Freigabe];
          break;
        case SubMainMenuTitles.DocumentManager:
          currentIndicator = indicator[IndicatorTypes.Doctype];
          break;
        case SubMainMenuTitles.Exports:
          currentIndicator = indicator[IndicatorTypes.Exports];
          break;
        case SubMainMenuTitles.Fines:
          currentIndicator = indicator[IndicatorTypes.Fines];
          break;
        case SubMainMenuTitles.CashAdvance:
          currentIndicator = indicator[IndicatorTypes.CashAdvance];
          break;
        case SubMainMenuTitles.VehicleRequests:
          currentIndicator = indicator[IndicatorTypes.VehicleRequests];
          break;
        case SubMainMenuTitles.Orders:
          currentIndicator = indicator[IndicatorTypes.Orders];
          break;
        case SubMainMenuTitles.LeasingContracts:
          currentIndicator = indicator[IndicatorTypes.LeasingContracts];
          break;
        case SubMainMenuTitles.ServiceProviderTask:
          currentIndicator = indicator[IndicatorTypes.YourQuestion];
          break;
        default:
          break;
      }

      isIndicatorShown = !!currentIndicator;
      indicatorValue = currentIndicator;
    }

    return {
      isIndicatorShown,
      indicatorValue
    };
  };

  const openPageHandler = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, link: string) => {
    if (e.ctrlKey || e.metaKey || e.button === 1) {
      return;
    }

    e.preventDefault();
    closePopover?.();
    setTimeout(() =>
      openUnsavedChangesModal(() => {
        goToFirstPage(link as PAGES);
        history.replace(location.pathname);
        history.push(link);
        dispatch(setSideMenuOpen(false));
      })
    );
  };

  const openExternalPage = () => {
    window.open(subMenuItem.links[0], '_blank', 'noopener,noreferrer');
  };

  const indicatorObj = checkIndicator(subMenuItem);
  if (subMenuItem.isVisible === false) {
    return null;
  }
  const isActive = subMenuItem.links.some(link => link === path && !(subMenuItem.hideIfParams && location.search));

  const handleTooltip = (showTooltip: boolean) => {
    if (!showMenuTitle) {
      setShowTooltip(showTooltip);
    }
  };

  const subMenuLinkItemStyle: CSSProperties = {
    marginTop: isFirstItem ? '10px' : 0,
    marginBottom: isLastItem ? '10px' : '0'
  };

  return (
    <>
      {subMenuItem.external ? (
        <Tooltip
          open={showTooltip}
          title={subMenuItem.title}
          onOpen={() => handleTooltip(true)}
          onClose={() => handleTooltip(false)}
          disableInteractive
        >
          <div>
            <StyledSubMenuLinkItem
              style={subMenuLinkItemStyle}
              $active={isActive}
              id={replaceGermanWordsToEnglish(subMenuItem.title) + 'SubItem'}
              onClick={openExternalPage}
            >
              <Tooltip title={subMenuItem.title}>
                <div>
                  <IconHover height='20px' hoveredColor={theme.colors.blackPearl}>
                    {cloneElement(subMenuItem.subMenuImg, {
                      color: theme.colors.grey600,
                      width: 20,
                      height: 20
                    })}
                  </IconHover>
                </div>
              </Tooltip>
              {showMenuTitle && (
                <>
                  <Distance side='20' />
                  <StyledMenuTitle className='sub-menu-item-title' $active={isActive}>
                    {subMenuItem.title}
                  </StyledMenuTitle>
                </>
              )}
              {subMenuItem.infoChip && showMenuTitle && (
                <>
                  <Distance side='15' />
                  <Tooltip title={subMenuItem.infoChipTooltip || ''}>
                    <div>
                      <InfoChip color={theme.colors.white} bgcolor={theme.colors.grey400} text={subMenuItem.infoChip} />
                    </div>
                  </Tooltip>
                </>
              )}
            </StyledSubMenuLinkItem>
          </div>
        </Tooltip>
      ) : (
        <Tooltip
          open={showTooltip}
          title={subMenuItem.title}
          onOpen={() => handleTooltip(true)}
          onClose={() => handleTooltip(false)}
          disableInteractive
        >
          <StyledSubMenuItem
            key={subMenuItem.title}
            style={subMenuLinkItemStyle}
            $active={isActive}
            to={subMenuItem.links[0]}
            onClick={event => openPageHandler(event, subMenuItem.links[0])}
            id={replaceGermanWordsToEnglish(subMenuItem.title) + 'SubItem'}
          >
            <Flex position='relative'>
              <IconHover height='20px' hoveredColor={theme.colors.blackPearl}>
                {cloneElement(subMenuItem.subMenuImg, {
                  color: theme.colors.grey600,
                  width: 20,
                  height: 20
                })}
              </IconHover>

              {Boolean(indicatorObj.isIndicatorShown) && !showMenuTitle && (
                <Tooltip title={subMenuItem.indicatorText || ''}>
                  <StyledIndicatorForCollapsedMenu />
                </Tooltip>
              )}
            </Flex>

            {showMenuTitle && (
              <>
                <Distance side='20' />
                <StyledMenuTitle className='sub-menu-item-title' $active={isActive}>
                  {subMenuItem.title}
                </StyledMenuTitle>
              </>
            )}

            {subMenuItem.infoChip && showMenuTitle && (
              <>
                <Distance side='15' />
                <Tooltip title={subMenuItem.infoChipTooltip || ''}>
                  <div>
                    <InfoChip color={theme.colors.white} bgcolor={theme.colors.grey400} text={subMenuItem.infoChip} />
                  </div>
                </Tooltip>
              </>
            )}

            {Boolean(indicatorObj.isIndicatorShown) && showMenuTitle && (
              <>
                <div className='mr-15' />
                <Tooltip title={subMenuItem.indicatorText || ''}>
                  <StyledIndicator>{indicatorObj.indicatorValue}</StyledIndicator>
                </Tooltip>
              </>
            )}
          </StyledSubMenuItem>
        </Tooltip>
      )}
      {subMenuItem.underline && !isLastItem && <Divider color='grey100' margin='5px 15px' />}
    </>
  );
};

export default SubMenuItemComponent;
